import { type TAdFoxBannerSizeProps, getCompleteSizes } from '@cian/adfox-component';
import { makeContextModule } from '@cian/react-utils';
import { type TDeviceType } from '@cian/ui-kit';

import { type FC, type JSX, useMemo } from 'react';

import { useListingLayoutIsNarrowSelector } from '@/packages/redux-state/store/slices/listingLayout';

import { type AdfoxContextData } from '../types/AdfoxContextData';

import { useAdfoxParameters } from './internal/hooks/useAdfoxParameters';
import { type PParams } from './types';

const contextData = makeContextModule<AdfoxContextData>({ contextName: 'AdfoxContextData' });

const {
  Context: { Provider },
} = contextData;

export const { useContext: useAdfoxContext, withContext: withAdfoxContext } = contextData;

export type AdfoxContextProviderProps = Omit<AdfoxContextData, 'params' | 'sizes' | 'isNarrow' | 'bannerDeviceType'> & {
  children: JSX.Element | JSX.Element[];
  pParams: PParams;
  size: TAdFoxBannerSizeProps;
  bannerDeviceType?: TDeviceType;
};

export const AdfoxContextProvider: FC<AdfoxContextProviderProps> = ({
  children,
  pParams,
  size,
  postViewAnalyticsData,
  serverData,
  bannerDeviceType = 'desktop',
  trackBannerAsHtml,
}) => {
  const params = useAdfoxParameters(serverData, pParams);

  const isNarrow = useListingLayoutIsNarrowSelector();

  const contextValue = useMemo<AdfoxContextData>(
    () => ({
      params,
      sizes: getCompleteSizes(size),
      postViewAnalyticsData,
      isNarrow,
      serverData,
      bannerDeviceType,
      trackBannerAsHtml,
    }),
    [bannerDeviceType, isNarrow, params, postViewAnalyticsData, serverData, trackBannerAsHtml, size],
  );

  return <Provider value={contextValue}>{children}</Provider>;
};
